import { createSlice, current, PayloadAction } from "@reduxjs/toolkit";
import {
  communicationSetsHandleFetch,
  compareInteractionNodesByOrdinality,
  guidGenerator,
  stateDeepCopy,
} from "../../helpers/DataUpdateHelper";
import CommunicationSet from "../../models/communications/CommunicationSet";
import Interval from "../../models/communications/Interval";
import IntervalSet from "../../models/communications/IntervalSet";
import Interaction from "../../models/interactions/Interaction";
import InteractionNode from "../../models/interactions/InteractionNode";
import InteractionNodeInterval from "../../models/interactions/InteractionNodeInterval";
import InteractionParentInterval from "../../models/interactions/InteractionParentInterval";
import InteractionIntervalSetting from "../../models/communications/InteractionIntervalSetting";
import { tableSliceActions } from "./TableSlice";
import { List } from "immutable";
import { DatabaseTable } from "../../models/BaseTable";

export interface CentralInteractionState {
  InteractionNodes: Array<InteractionNode>;
  Interactions: Array<Interaction>;
  InteractionNodeIntervals: Array<InteractionNodeInterval>;
  Intervals: Array<Interval>;
  IntervalSets: Array<IntervalSet>; // all possible intervalsets to choose from that should show in dropdowns throughout app
  InteractionParentIntervals: Array<InteractionParentInterval>;
  CommunicationSets: Array<CommunicationSet>; // all possible condition sets to choose from that should show in dropdowns throughout app (individual communications are lazy loaded)
  RootInteractionID: number; // the root interaction of the selected set. THIS SHOULD NEVER CHANGE
  CurrentRootInteractionID: number; // the currently selected interactionID - the root of the currently rendered tree
  CurrentParent: number; // the currently selected interactions' direct parent
  Loaded: boolean; // whether interactions have actually been loaded yet
  InteractionNodeIntervalsToDelete: Array<InteractionNodeInterval>;
}

// Normally, we would probaably initalize this empty and fetch data
// until API is finalized, use dummy data above
export const initialState: CentralInteractionState = {
  InteractionNodes: [],
  Interactions: [],
  InteractionNodeIntervals: [],
  Intervals: [],
  IntervalSets: [],
  InteractionParentIntervals: [],
  CommunicationSets: [],
  RootInteractionID: 0,
  CurrentRootInteractionID: 6,
  CurrentParent: 0, // dont need yet?
  Loaded: false,
  InteractionNodeIntervalsToDelete: [],
};

const interactionTreeSlice = createSlice({
  name: "interactionTree",
  initialState,
  reducers: {
    replaceState(
      state,
      action: PayloadAction<CentralInteractionState | undefined>,
    ) {
      if (typeof action.payload === "undefined") {
        return initialState;
      } else {
        return action.payload;
      }
    },
    // interactions tree is always rendered from a selected root node
    // updating this node displays only the selected node and its children in context
    updateRootInteractionID(state, action: PayloadAction<number>) {
      state.RootInteractionID = action.payload;
    },
    getStateStatus(state) {
      console.log(current(state));
    },
    updateCurrentRootInteractionID(state, action: PayloadAction<number>) {
      state.CurrentRootInteractionID = action.payload;
    },
    setInteractionNodes(state, action: PayloadAction<any>) {
      const tempInteractionNodes = action.payload.interactionNodes;
      if (action.payload.append) {
        state.InteractionNodes = tempInteractionNodes.concat(
          state.InteractionNodes,
        );
      } else {
        state.InteractionNodes = tempInteractionNodes;
      }
    },
    setInteractionNodeIntervalsToDelete(state, action: PayloadAction<any>) {
      const tempInteractionNodeIntervalsToDelete =
        action.payload.InteractionNodeIntervalsToDelete;

      if (action.payload.append) {
        state.InteractionNodeIntervalsToDelete =
          tempInteractionNodeIntervalsToDelete.concat(
            state.InteractionNodeIntervalsToDelete,
          );
      } else {
        state.InteractionNodeIntervalsToDelete =
          tempInteractionNodeIntervalsToDelete;
      }
    },
    updateInteractionNodeOrdinalityByParentAndNode(
      state,
      action: PayloadAction<any>,
    ) {
      const nodeid = action.payload.nodeid;
      const parentid = action.payload.parentid;
      const oldOrdinal = action.payload.oldOrdinal;
      const newOrdinal = action.payload.newOrdinal;
      const deleteNode = action.payload.deleteNode;
      let interactionNodesCopy = stateDeepCopy(state.InteractionNodes);

      if (deleteNode) {
        interactionNodesCopy = interactionNodesCopy.map(
          (interactionNode: InteractionNode) => {
            const interactionNodeCopy = stateDeepCopy(interactionNode);

            if (
              interactionNodeCopy.ordinal > oldOrdinal &&
              interactionNodeCopy.parent_interaction_id == parentid
            ) {
              interactionNodeCopy.ordinal = interactionNodeCopy.ordinal - 1;
            }
            return interactionNodeCopy;
          },
        );
      } else {
        interactionNodesCopy = interactionNodesCopy.map(
          (interactionNode: InteractionNode) => {
            const interactionNodeCopy = stateDeepCopy(interactionNode);

            // First, update the row with the old old_ordinal to the new new_ordinal
            // if inserting a brand new node, new_ordinal and old_ordinal will be the same
            if (
              interactionNodeCopy.ordinal == oldOrdinal &&
              interactionNodeCopy.parent_interaction_id == parentid &&
              interactionNodeCopy.id == nodeid
            ) {
              interactionNodeCopy.ordinal = newOrdinal;
            }
            // if new > old  (move middle node to the end)
            if (newOrdinal > oldOrdinal) {
              // for any node located before the original spot the selected node was previously in - dont do anything
              // for any node equal to or above to the original spot (that also isnt this specific node) - decrement by 1
              if (
                interactionNodeCopy.ordinal >= oldOrdinal &&
                interactionNodeCopy.ordinal <= newOrdinal &&
                interactionNodeCopy.parent_interaction_id == parentid &&
                interactionNodeCopy.id != nodeid
              ) {
                interactionNodeCopy.ordinal = interactionNodeCopy.ordinal - 1;
              }
              // if new = old OR old > new (brand new insertion, or e.g. moving end node to somewhere in middle)
              // and not THIS node, increment by 1
            } else {
              if (
                interactionNodeCopy.ordinal >= newOrdinal &&
                interactionNodeCopy.parent_interaction_id == parentid &&
                interactionNodeCopy.id != nodeid
              ) {
                interactionNodeCopy.ordinal = interactionNodeCopy.ordinal + 1;
              }
            }
            return interactionNodeCopy;
          },
        );
      }

      interactionNodesCopy = interactionNodesCopy.sort(
        compareInteractionNodesByOrdinality,
      );

      // create map of unique parentIDs to parse through
      // so we can resolve the ordinality of their children
      const resultArray = [
        ...new Set(
          interactionNodesCopy.map((item: InteractionNode) => item.parent_interaction_id),
        ),
      ];
      resultArray.forEach((parentID) => {
        // remove all gaps in ordinal field
        let newOrdinalVal = 1;
        for (const node of interactionNodesCopy) {
          if (node.parent_interaction_id == parentID) {
            node.ordinal = newOrdinalVal;
            newOrdinalVal++;
          }
        }
      });
      state.InteractionNodes = interactionNodesCopy;
    },
    fixOrdinalityForParent(state, action: PayloadAction<any>) {
      const parentID = action.payload.parentID;
      let newOrdinalVal = 1;
      const interactionNodesCopy = stateDeepCopy(state.InteractionNodes);
      for (const node of interactionNodesCopy) {
        if (node.parent_interaction_id == parentID) {
          node.ordinal = newOrdinalVal;
          newOrdinalVal++;
        }
      }
      state.InteractionNodes = interactionNodesCopy;
    },
    setInteractions(state, action: PayloadAction<any>) {
      const tempInteractions = action.payload.interactions;
      if (action.payload.append) {
        state.Interactions = tempInteractions.concat(state.Interactions);
      } else {
        state.Interactions = tempInteractions;
      }
    },
    setInteractionNodeIntervals(state, action: PayloadAction<any>) {
      const tempInteractionNodeIntervals =
        action.payload.interactionNodeIntervals;
      if (action.payload.append) {
        state.InteractionNodeIntervals = tempInteractionNodeIntervals.concat(
          state.InteractionNodeIntervals,
        );
      } else {
        state.InteractionNodeIntervals = tempInteractionNodeIntervals;
      }
    },
    deleteInteractionNodeIntervals(state, action: PayloadAction<any>) {
      const id = action.payload.interaction_node_interval_id;
      const objIndex = state.InteractionNodeIntervals.findIndex(
        (obj) => obj.id === id,
      );

      const arrCopy = Array.from(state.InteractionNodeIntervals);
      arrCopy.splice(objIndex, 1);

      state.InteractionNodeIntervals = arrCopy;
    },
    deleteInteractionNodeIntervalsViaIntervalSettings(
      state,
      action: PayloadAction<any>,
    ) {
      const intervalSettings: Array<InteractionIntervalSetting> = stateDeepCopy(
        action.payload.intervalSettings,
      );
      const arrCopy = Array.from(state.InteractionNodeIntervals);

      intervalSettings.forEach(
        (intervalSetting: InteractionIntervalSetting) => {
          const objIndex = arrCopy.findIndex(
            (obj) => obj.id === intervalSetting.interaction_node_interval_id,
          );
          arrCopy.splice(objIndex, 1);
        },
      );
      state.InteractionNodeIntervals = arrCopy;
    },
    deleteInteractionNode(state, action: PayloadAction<any>) {
      const id = action.payload.id;
      const objIndex = state.InteractionNodes.findIndex((obj) => obj.id === id);

      const arrCopy = Array.from(state.InteractionNodes);
      arrCopy.splice(objIndex, 1);

      state.InteractionNodes = arrCopy;
    },
    deleteInteraction(state, action: PayloadAction<any>) {
      const id = action.payload.id;
      const objIndex = state.Interactions.findIndex((obj) => obj.id === id);

      const arrCopy = Array.from(state.Interactions);
      arrCopy.splice(objIndex, 1);

      state.Interactions = arrCopy;
    },
    // delete the interval as well as any InteractionNodeIntervals it was associated with
    deleteInterval(state, action: PayloadAction<any>) {
      const id = action.payload.id;

      // get all InteractionNodeIntervals this interval is associated with, so we can delete them
      const interactionNodeIntervals = state.InteractionNodeIntervals.filter(
        (interactionNodeInterval) => {
          return interactionNodeInterval.interval_id === id;
        },
      );
      const objIndex = state.Intervals.findIndex((obj) => obj.id === id);
      const arrCopy = Array.from(state.Intervals);
      arrCopy.splice(objIndex, 1);

      // update intervals state
      state.Intervals = arrCopy;

      // iterate through relevant interaction node intervals
      const arrCopy2 = Array.from(state.InteractionNodeIntervals);

      interactionNodeIntervals.forEach((interactionNodeIntervals: any) => {
        const objIndex = arrCopy2.findIndex(
          (obj) => obj.id === interactionNodeIntervals.id,
        );

        // update current state copy with version that has removed the ini
        arrCopy2.splice(objIndex, 1);
      });
      state.InteractionNodeIntervals = arrCopy2;
    },

    addInteractionNodeInterval(state, action: PayloadAction<any>) {
      const newInteractionNodeInterval = action.payload;
      state.InteractionNodeIntervals.push(newInteractionNodeInterval);
    },
    addInterval(state, action: PayloadAction<any>) {
      const newInterval = action.payload;
      state.Intervals.push(newInterval);
    },
    setIntervals(state, action: PayloadAction<any>) {
      const tempIntervals = action.payload.intervals;

      if (action.payload.append) {
        state.Intervals = tempIntervals.concat(state.Intervals);
      } else {
        state.Intervals = tempIntervals;
      }
    },
    setIntervalSets(state, action: PayloadAction<any>) {
      const tempIntervalSets = action.payload.intervalSets;
      if (action.payload.append) {
        state.IntervalSets = tempIntervalSets.concat(state.IntervalSets);
      } else {
        state.IntervalSets = tempIntervalSets;
      }
    },
    setCommunicationSets(state, action: PayloadAction<any>) {
      const tempCommunicationSets = action.payload.communicationSets;
      if (action.payload.append) {
        state.CommunicationSets = tempCommunicationSets.concat(
          state.CommunicationSets,
        );
      } else {
        state.CommunicationSets = tempCommunicationSets;
      }
      console.log(current(state));
    },
    setInteractionParentIntervals(state, action: PayloadAction<any>) {
      const tempInteractionParentIntervals =
        action.payload.interactionParentIntervals;
      if (action.payload.append) {
        state.InteractionParentIntervals =
          tempInteractionParentIntervals.concat(
            state.InteractionParentIntervals,
          );
      } else {
        state.InteractionParentIntervals = tempInteractionParentIntervals;
      }
    },
    addNewInteraction(state, action: PayloadAction<any>) {
      // TODO - this tempID will need to be replaced by the real ID once the DB is updated
      let title = "Enter New Title";
      if (action.payload.title) {
        title = action.payload.title;
      }

      const type = "cs1survey";

      let properties = { type: type };
      if (action.payload.properties) {
        properties = action.payload.properties;
      }

      // set random ID if one not given
      let newTempID = guidGenerator();
      if (action.payload.id) {
        newTempID = action.payload.id;
      }
      const newPlaceholderInteraction = {
        id: newTempID,
        title,
        properties,
        modified: new Date(),
      } as Interaction;
      const deSerializedInteraction = stateDeepCopy(newPlaceholderInteraction);
      state.Interactions.push(deSerializedInteraction);
    },
    updateInteraction(state, action: PayloadAction<any>) {
      const updateInteractionID = action.payload.updateInteractionID;
      const interactionID = action.payload.interactionID;
      const title = action.payload.title;
      const properties = action.payload.properties;
      const objIndex = state.Interactions.findIndex(
        (obj) => obj.id == interactionID,
      );

      // Right now, we are committing to keep type unmutable
      // BUT that might change in the future....
      // if(type){
      //   state.Interactions[objIndex].type = type
      // }
      if (updateInteractionID && objIndex != -1) {
        state.Interactions[objIndex].id = parseInt(updateInteractionID);
      }

      if (title && objIndex != -1) {
        state.Interactions[objIndex].title = title;
      }

      if (properties && objIndex != -1) {
        state.Interactions[objIndex].properties = properties;
      }
    },
    updateCommSet(state, action: PayloadAction<any>) {
      const id = action.payload.id;
      const name = action.payload.name;
      const objIndex = state.CommunicationSets.findIndex((obj) => obj.id == id);
      if (name && objIndex != -1) {
        state.CommunicationSets[objIndex].name = name;
      }
    },
    updateInteractionNodeWithNewChild(state, action: PayloadAction<any>) {
      const childInteractionID = action.payload.childInteractionID;
      const newChildInteractionID = action.payload.newChildInteractionID;

      const objIndex = (state.InteractionNodes as InteractionNode[]).findIndex(
        (interactionNode: InteractionNode) =>
          interactionNode.child_interaction_id === childInteractionID,
      );
      if (objIndex != -1) {
        state.InteractionNodes[objIndex].child_interaction_id = parseInt(
          newChildInteractionID,
        );
      }
    },
    updateInteractionNodeInterval(state, action: PayloadAction<any>) {
      const interactionNodeIntervalID = action.payload.id;
      const communication_set_id = action.payload.communication_set_id;
      const condition_id = action.payload.condition_id;
      const deleteFromInterval = action.payload.delete;

      const objIndex = state.InteractionNodeIntervals.findIndex(
        (obj) => obj.id === interactionNodeIntervalID,
      );

      // really, comm set id is the only setting on a node interval that can be updated
      if (
        communication_set_id ||
        (deleteFromInterval && !communication_set_id)
      ) {
        state.InteractionNodeIntervals[objIndex].communication_set_id =
          communication_set_id;
      }
      if (typeof condition_id === "number" || condition_id === null) {
        console.log(condition_id);
        state.InteractionNodeIntervals[objIndex].condition_id = condition_id;
      }
    },
    updateInterval(state, action: PayloadAction<any>) {
      const intervalID = action.payload.id;
      const name = action.payload.name;
      const start_day = action.payload.start_day;
      const end_day = action.payload.end_day;
      const interval_set_id = action.payload.interval_set_id;
      const communication_set_id = action.payload.communication_set_id;
      const deleteFromInterval = action.payload.delete;

      const objIndex = state.Intervals.findIndex(
        (obj) => obj.id === intervalID,
      );
      if (name) {
        state.Intervals[objIndex].name = name;
      }

      if (start_day) {
        state.Intervals[objIndex].start_day = start_day;
      }

      if (end_day) {
        state.Intervals[objIndex].end_day = end_day;
      }

      if (interval_set_id) {
        state.Intervals[objIndex].interval_set_id = interval_set_id;
      }

      // update communication_set, or clear it out if delete flag selected
      if (
        communication_set_id ||
        (deleteFromInterval && !communication_set_id)
      ) {
        state.Intervals[objIndex].communication_set_id = communication_set_id;
      }
      // console.log(current(state));
    },

    updateIntervalSet(state, action: PayloadAction<any>) {
      const id = action.payload.id;
      const name = action.payload.name;
      const shared = action.payload.shared;

      const allIntervalSetInstances = state.IntervalSets.filter(
        (obj) => obj.id === id,
      );
      const intervalSetsStateCopy: IntervalSet[] = stateDeepCopy(
        state.IntervalSets,
      );
      intervalSetsStateCopy.forEach((intervalSetCopy, index) => {
        // if objindex exists, we need to update this object
        const objIndex = allIntervalSetInstances.findIndex(
          (obj) => obj.id === intervalSetCopy.id,
        );
        if (objIndex !== -1) {
          if (name) {
            intervalSetsStateCopy[index].name = name;
          }
          intervalSetsStateCopy[index].shared = shared;
        }
      });
      (state.IntervalSets as any) = intervalSetsStateCopy;
    },
    updateInteractionParentInterval(state, action: PayloadAction<any>) {
      const id = action.payload.id;
      const communication_set_id = action.payload.communication_set_id;
      const deleteFromInterval = action.payload.delete;
      // find obj index in state
      const objIndex = state.InteractionParentIntervals.findIndex(
        (obj) => obj.id === id,
      );

      // make the update
      if (
        communication_set_id ||
        (deleteFromInterval && !communication_set_id)
      ) {
        state.InteractionParentIntervals[objIndex].communication_set_id =
          communication_set_id;
      }
    },
    addInteractionParentInterval(state, action: PayloadAction<any>) {
      const id = action.payload.id;
      const interaction_parent_id = parseInt(
        action.payload.interaction_parent_id,
      );
      const communication_set_id = action.payload.communication_set_id;
      const interval_id = action.payload.interval_id;

      // make new interaction parent interval
      let newInteractionParentInterval = new InteractionParentInterval(
        id,
        interaction_parent_id,
        interval_id,
        communication_set_id,
        "1900-01-01 00:00:00",
      );
      newInteractionParentInterval = stateDeepCopy(
        newInteractionParentInterval,
      );
      // make the update
      state.InteractionParentIntervals.push(newInteractionParentInterval);
      // console.log(current(state));
    },

    addNewInteractionNode(state, action: PayloadAction<any>) {
      // console.log(current(state));
      // for interaction nodes, new ones need to be placed directly as a child of the parent for which they are being added under
      // so we need to figure out where we "are" in the tree first, and end the parent as that
      const parentID = action.payload.parentID;
      const setID = action.payload.setID;
      // Also, a node will almost always be added in conjunction with an interaction (new or existing)
      const childID = action.payload.childID;
      const ordinal = action.payload.ordinal;
      const condition = action.payload.condition;
      const communication = action.payload.communication;

      // TODO - this tempID will need to be replaced by the real ID once the DB is updated
      let newTempID = guidGenerator();
      if (action.payload.id) {
        newTempID = action.payload.id;
      }
      const newPlaceholderInteractionNode = {
        id: newTempID,
        set_interaction_id: setID,
        parent_interaction_id : parentID,
        ordinal,
        child_interaction_id: childID,
        condition_id: condition,
        modified: new Date(),
      } as InteractionNode;
      const deSerializedInteractionNode = stateDeepCopy(
        newPlaceholderInteractionNode,
      );
      state.InteractionNodes.push(deSerializedInteractionNode);
    },

    addNewIntervalSet(state, action: PayloadAction<any>) {
      // console.log(current(state));
      // for interaction nodes, new ones need to be placed directly as a child of the parent for which they are being added under
      // so we need to figure out where we "are" in the tree first, and end the parent as that
      let newTempID = guidGenerator();

      const name = action.payload.name;
      const shared = action.payload.shared;
      const currentParent = action.payload.currentParent; // needed for "new interval set" placeholders, so we know where any new interactions on this set should go in the tree
      const currentSet = action.payload.currentSet; // needed for "new interval set" placeholders, so we know where any new interactions on this set should go in the tree

      // TODO - this tempID will need to be replaced by the real ID once the DB is updated
      if (action.payload.id) {
        newTempID = action.payload.id;
      }
      const newPlaceholderIntervalSet = {
        id: newTempID,
        name: name,
        shared: shared,
        modified: new Date(),
        currentParent: currentParent,
        currentSet: currentSet,
      } as IntervalSet;
      const deSerializedIntervalSet = stateDeepCopy(newPlaceholderIntervalSet);
      state.IntervalSets.push(deSerializedIntervalSet);
      // console.log(current(state));
    },
    deleteIntervalSet(state, action: PayloadAction<any>) {
      // id of item to delete
      const id = action.payload.id;
      const intervalSetsStateCopy = stateDeepCopy(state.IntervalSets);
      // delete all instances
      for (const i in intervalSetsStateCopy) {
        if (intervalSetsStateCopy[i].id === id) {
          intervalSetsStateCopy.splice(i, 1);
        }
      }
      // save copy to state
      state.IntervalSets = intervalSetsStateCopy;
    },
    // generate a new interaction and corresponding node. In most cases we will need to use this one
    addNewInteractionAndNode(state, action: PayloadAction<any>) {
      const title = action.payload.title;
      const properties = action.payload.properties;

      const newTempID_Interaction = guidGenerator();
      const newPlaceholderInteraction = {
        id: newTempID_Interaction,
        title,
        properties,
        modified: new Date(),
      } as Interaction; // TODO - dont hardcode properties
      const deSerializedInteraction = stateDeepCopy(newPlaceholderInteraction);
      state.Interactions.push(deSerializedInteraction);

      // for interaction nodes, new ones need to be placed directly as a child of the parent for which they are being added under
      // so we need to figure out where we "are" in the tree first, and end the parent as that
      const parentID = action.payload.parentID;
      const setID = action.payload.setID;
      // Also, a node will almost always be added in conjunction with an interaction (new or existing)
      const ordinal = action.payload.ordinal;
      const condition = action.payload.condition;

      // TODO - this tempID will need to be replaced by the real ID once the DB is updated
      const newTempID_InteractionNode = guidGenerator();
      const newPlaceholderInteractionNode = {
        id: newTempID_InteractionNode,
        set_interaction_id : setID,
        parent_interaction_id: parentID,
        ordinal: ordinal,
        child_interaction_id : newTempID_Interaction,
        condition_id: condition,
        modified: new Date(),
      } as InteractionNode;
      const deSerializedInteractionNode = stateDeepCopy(
        newPlaceholderInteractionNode,
      );
      state.InteractionNodes.push(deSerializedInteractionNode);
    },
    updateInteractionNode(state, action: PayloadAction<any>) {
      const id = action.payload.id;
      const condition_id = action.payload.condition_id;
      const ordinal = action.payload.ordinal;
      const parent = action.payload.parent;

      const objIndex = state.InteractionNodes.findIndex((obj) => obj.id === id);
      if (condition_id) {
        state.InteractionNodes[objIndex].condition_id = condition_id;
      }
      if (ordinal) {
        state.InteractionNodes[objIndex].ordinal = ordinal;
      }
      if (parent) {
        state.InteractionNodes[objIndex].parent_interaction_id = parent;
      }
    },
    updateLoaded(state, action: PayloadAction<boolean>) {
      state.Loaded = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(tableSliceActions.insertTableRow, (state, action) => {
      const data = action.payload.data;
      const table_name = action.payload.table.tableName;
      if (table_name === "Interaction") {
        const interaction = data as Interaction;
        const objIndex = state.Interactions.findIndex(
          (obj) => obj.id == interaction.id,
        );
        if (objIndex === -1) {
          state.Interactions.push(interaction);
        } else {
          state.Interactions[objIndex] = interaction;
        }
      } else if (table_name === "InteractionNode") {
        const interaction_node = data as InteractionNode;
        const objIndex = state.InteractionNodes.findIndex(
          (obj) => obj.id == interaction_node.id,
        );
        if (objIndex === -1) {
          state.InteractionNodes.push(interaction_node);
        } else {
          state.InteractionNodes[objIndex] = interaction_node;
        }
      } else if (table_name === "IntervalSet") {
        const interval_set = data as IntervalSet as any;
        const objIndex = state.IntervalSets.findIndex(
          (obj) => obj.id == interval_set.id,
        );
        if (objIndex === -1) {
          state.IntervalSets.push(interval_set);
        } else {
          state.IntervalSets[objIndex] = interval_set;
        }
      } else if (table_name === "Interval") {
        const interval = data as Interval;
        const objIndex = state.Intervals.findIndex(
          (obj) => obj.id == interval.id,
        );
        if (objIndex === -1) {
          state.Intervals.push(interval);
        } else {
          state.Intervals[objIndex] = interval;
        }
      } else if (table_name === "InteractionNodeInterval") {
        const interaction_node_interval = data as InteractionNodeInterval;
        const objIndex = state.InteractionNodeIntervals.findIndex(
          (obj) => obj.id == interaction_node_interval.id,
        );
        if (objIndex === -1) {
          state.InteractionNodeIntervals.push(interaction_node_interval);
        } else {
          state.InteractionNodeIntervals[objIndex] = interaction_node_interval;
        }
      } else if (table_name === "CommunicationSet") {
        const communication_set = data as CommunicationSet;
        const objIndex = state.CommunicationSets.findIndex(
          (obj) => obj.id == communication_set.id,
        );
        if (objIndex === -1) {
          state.CommunicationSets.push(communication_set);
        } else {
          state.CommunicationSets[objIndex] = communication_set;
        }
      }
    });
    builder.addCase(tableSliceActions.updateTableRow, (state, action) => {
      const data = action.payload.data;
      const table_name = action.payload.table.tableName;
      if (table_name === "Interaction") {
        const interaction = data as Interaction;
        const objIndex = state.Interactions.findIndex(
          (obj) => obj.id == interaction.id,
        );
        if (objIndex === -1) {
          state.Interactions.push(interaction);
        } else {
          state.Interactions[objIndex] = interaction;
        }
      } else if (table_name === "InteractionNode") {
        const interaction_node = data as InteractionNode;
        const objIndex = state.InteractionNodes.findIndex(
          (obj) => obj.id == interaction_node.id,
        );
        if (objIndex === -1) {
          state.InteractionNodes.push(interaction_node);
        } else {
          state.InteractionNodes[objIndex] = interaction_node;
        }
      } else if (table_name === "IntervalSet") {
        const interval_set = data as IntervalSet as any;
        const objIndex = state.IntervalSets.findIndex(
          (obj) => obj.id == interval_set.id,
        );
        if (objIndex === -1) {
          state.IntervalSets.push(interval_set);
        } else {
          state.IntervalSets[objIndex] = interval_set;
        }
      } else if (table_name === "Interval") {
        const interval = data as Interval;
        const objIndex = state.Intervals.findIndex(
          (obj) => obj.id == interval.id,
        );
        if (objIndex === -1) {
          state.Intervals.push(interval);
        } else {
          state.Intervals[objIndex] = interval;
        }
      } else if (table_name === "InteractionNodeInterval") {
        const interaction_node_interval = data as InteractionNodeInterval;
        const objIndex = state.InteractionNodeIntervals.findIndex(
          (obj) => obj.id == interaction_node_interval.id,
        );
        if (objIndex === -1) {
          state.InteractionNodeIntervals.push(interaction_node_interval);
        } else {
          state.InteractionNodeIntervals[objIndex] = interaction_node_interval;
        }
      } else if (table_name === "CommunicationSet") {
        const communication_set = data as CommunicationSet;
        const objIndex = state.CommunicationSets.findIndex(
          (obj) => obj.id == communication_set.id,
        );
        if (objIndex === -1) {
          state.CommunicationSets.push(communication_set);
        } else {
          state.CommunicationSets[objIndex] = communication_set;
        }
      }
    });
    builder.addCase(tableSliceActions.deleteTableRow, (state, action) => {
      const table_name = action.payload.table.tableName;
      const id = action.payload.pk;
      if (table_name === "Interaction") {
        const objIndex = state.Interactions.findIndex((obj) => obj.id == id);
        if (objIndex > -1) {
          state.Interactions.splice(objIndex, 1);
        }
      } else if (table_name === "InteractionNode") {
        const objIndex = state.InteractionNodes.findIndex(
          (obj) => obj.id == id,
        );
        if (objIndex > -1) {
          state.InteractionNodes.splice(objIndex, 1);
        }
      } else if (table_name === "IntervalSet") {
        const objIndex = state.IntervalSets.findIndex((obj) => obj.id == id);
        if (objIndex > -1) {
          state.IntervalSets.splice(objIndex, 1);
        }
      } else if (table_name === "Interval") {
        const objIndex = state.Intervals.findIndex((obj) => obj.id == id);
        if (objIndex > -1) {
          state.Intervals.splice(objIndex, 1);
        }
      } else if (table_name === "InteractionNodeInterval") {
        const objIndex = state.InteractionNodeIntervals.findIndex(
          (obj) => obj.id == id,
        );
        if (objIndex > -1) {
          state.InteractionNodeIntervals.splice(objIndex, 1);
        }
      } else if (table_name === "CommunicationSet") {
        const objIndex = state.CommunicationSets.findIndex(
          (obj) => obj.id == id,
        );
        if (objIndex > -1) {
          state.CommunicationSets.splice(objIndex, 1);
        }
      }
    });
  },
});

/* ACTION CREATORS/THUNKS */

// interface for action creator thunk for generic interaction tree fetches
export interface InteractionsFetchProps {
  setID: number;
  append: boolean;
  parentID: null | number;
  updateBreadcrumbs: boolean;
  directAncestorNode: InteractionNode | null;
}

export const interactionTreeSliceActions = interactionTreeSlice.actions;

export default interactionTreeSlice;
